import type { AxiosRequestConfig } from 'axios';
import { getRequest, postRequest, getValidated } from '@/utils/api';
import { playerDataSchema } from '@/schemas/player';
import { cardsListSchema } from '@/schemas/cards';
import { topPlayersSchema } from '@/schemas/topPlayers';
import { nextGamesSchema } from '@/schemas/nextGames';
import { statsSummarySchema } from '@/schemas/stats';
import { sessionSchema } from '@/schemas/auth';
import {
  PLAYER_ENDPOINTS,
  MANAGER_ENDPOINTS,
  MARKET_ENDPOINTS,
  DASHBOARD_ENDPOINTS,
  AUTH_ENDPOINTS,
  QUERY_PARAMS,
  DEFAULT_VALUES,
  SEARCH_DEFAULTS,
} from '@/constants/api';
import type {
  ApiMutationResponse,
  GetCardsListResponse,
  GetManagerCardsResponse,
  GetNextGamesResponse,
  GetNbaLineupResponse,
  GetNbaPlayerResponse,
  GetPlayerCardsResponse,
  GetPlayerResponse,
  GetPlayerScoresResponse,
  GetSessionResponse,
  GetStatsSummaryResponse,
  GetTopNbaPlayersResponse,
  GetTopPlayersResponse,
  QueryParamsString,
  SearchPlayersResponse,
} from '@/types/api';

// Player-related endpoints
export const playerAPI = {
  getPlayer: (slug: string, config?: AxiosRequestConfig) =>
    getValidated<GetPlayerResponse>(PLAYER_ENDPOINTS.GET_PLAYER(slug), playerDataSchema, config),

  getNbaPlayer: (slug: string, config?: AxiosRequestConfig) =>
    getRequest<GetNbaPlayerResponse>(PLAYER_ENDPOINTS.GET_NBA_PLAYER(slug), config),

  getPlayerScores: (slug: string, params: QueryParamsString, config?: AxiosRequestConfig) =>
    getRequest<GetPlayerScoresResponse>(PLAYER_ENDPOINTS.GET_PLAYER_SCORES(slug, params), config),

  getPlayerNextGames: (slug: string, params: QueryParamsString, config?: AxiosRequestConfig) =>
    getValidated<GetNextGamesResponse>(
      PLAYER_ENDPOINTS.GET_PLAYER_NEXT_GAMES(slug, params),
      nextGamesSchema,
      config,
    ),

  getPlayerListed: (slug: string, rarity: string, limit: number, config?: AxiosRequestConfig) =>
    getRequest<GetPlayerCardsResponse>(
      PLAYER_ENDPOINTS.GET_PLAYER_LISTED(slug, buildPlayerCardsParams(rarity, limit)),
      config,
    ),

  getPlayerAuctions: (slug: string, rarity: string, limit: number, config?: AxiosRequestConfig) =>
    getRequest<GetPlayerCardsResponse>(
      PLAYER_ENDPOINTS.GET_PLAYER_AUCTIONS(slug, buildPlayerCardsParams(rarity, limit)),
      config,
    ),

  getPlayerRewards: (slug: string, rarity: string, limit: number, config?: AxiosRequestConfig) =>
    getRequest<GetPlayerCardsResponse>(
      PLAYER_ENDPOINTS.GET_PLAYER_REWARDS(slug, buildPlayerCardsParams(rarity, limit)),
      config,
    ),

  getPlayerInstant: (slug: string, rarity: string, limit: number, config?: AxiosRequestConfig) =>
    getRequest<GetPlayerCardsResponse>(
      PLAYER_ENDPOINTS.GET_PLAYER_INSTANT(slug, buildPlayerCardsParams(rarity, limit)),
      config,
    ),

  updatePlayerScores: (slug: string, config?: AxiosRequestConfig) =>
    postRequest<ApiMutationResponse>(PLAYER_ENDPOINTS.UPDATE_PLAYER_SCORES(slug), config),

  resetPlayerScores: (slug: string, config?: AxiosRequestConfig) =>
    postRequest<ApiMutationResponse>(PLAYER_ENDPOINTS.RESET_PLAYER_SCORES(slug), config),

  getTopPlayers: (params: QueryParamsString, config?: AxiosRequestConfig) =>
    getValidated<GetTopPlayersResponse>(
      PLAYER_ENDPOINTS.GET_TOP_PLAYERS(params),
      topPlayersSchema,
      config,
    ),

  getTopNbaPlayers: (params: QueryParamsString, config?: AxiosRequestConfig) =>
    getRequest<GetTopNbaPlayersResponse>(PLAYER_ENDPOINTS.GET_TOP_NBA_PLAYERS(params), config),

  searchPlayers: (searchText: string, config?: AxiosRequestConfig) => {
    const params = buildQueryParams({
      [QUERY_PARAMS.OFFSET]: SEARCH_DEFAULTS.OFFSET,
      [QUERY_PARAMS.LIMIT]: SEARCH_DEFAULTS.LIMIT,
      [QUERY_PARAMS.SEARCH_TEXT]: searchText,
    });
    return getRequest<SearchPlayersResponse>(PLAYER_ENDPOINTS.SEARCH_PLAYERS(params), config);
  },
};

// Manager-related endpoints
export const managerAPI = {
  getManagerCards: (slug: string, params: QueryParamsString, config?: AxiosRequestConfig) =>
    getRequest<GetManagerCardsResponse>(MANAGER_ENDPOINTS.GET_MANAGER_CARDS(slug, params), config),

  getNbaLineup: (slug: string, params: QueryParamsString, config?: AxiosRequestConfig) =>
    getRequest<GetNbaLineupResponse>(MANAGER_ENDPOINTS.GET_NBA_LINEUP(slug, params), config),

  refreshManagerCard: (slug: string, cardSlug: string, config?: AxiosRequestConfig) =>
    postRequest<ApiMutationResponse>(
      MANAGER_ENDPOINTS.REFRESH_MANAGER_CARD(slug, cardSlug),
      config,
    ),

  refreshAllManagerCards: (slug: string, config?: AxiosRequestConfig) =>
    postRequest<ApiMutationResponse>(MANAGER_ENDPOINTS.REFRESH_ALL_MANAGER_CARDS(slug), config),
};

// Market-related endpoints
export const marketAPI = {
  getAllCards: (params: QueryParamsString, config?: AxiosRequestConfig) =>
    getValidated<GetCardsListResponse>(
      MARKET_ENDPOINTS.GET_ALL_CARDS(params),
      cardsListSchema,
      config,
    ),

  getListedCards: (params: QueryParamsString, config?: AxiosRequestConfig) =>
    getValidated<GetCardsListResponse>(
      MARKET_ENDPOINTS.GET_LISTED_CARDS(params),
      cardsListSchema,
      config,
    ),

  getInstantCards: (params: QueryParamsString, config?: AxiosRequestConfig) =>
    getValidated<GetCardsListResponse>(
      MARKET_ENDPOINTS.GET_INSTANT_CARDS(params),
      cardsListSchema,
      config,
    ),

  getAuctions: (params: QueryParamsString, config?: AxiosRequestConfig) =>
    getValidated<GetCardsListResponse>(
      MARKET_ENDPOINTS.GET_AUCTIONS(params),
      cardsListSchema,
      config,
    ),

  getRewards: (params: QueryParamsString, config?: AxiosRequestConfig) =>
    getValidated<GetCardsListResponse>(
      MARKET_ENDPOINTS.GET_REWARDS(params),
      cardsListSchema,
      config,
    ),
};

// Dashboard-related endpoints
export const dashboardAPI = {
  getStatsSummary: (config?: AxiosRequestConfig) =>
    getValidated<GetStatsSummaryResponse>(
      DASHBOARD_ENDPOINTS.GET_STATS_SUMMARY(),
      statsSummarySchema,
      config,
    ),
};

// Sorare-connection endpoints. Starting the OAuth flow is deliberately absent: it is a
// full-page navigation, not an XHR — see `buildSorareConnectUrl` in constants/api.ts.
export const authAPI = {
  getSession: (config?: AxiosRequestConfig) =>
    getValidated<GetSessionResponse>(AUTH_ENDPOINTS.GET_SESSION(), sessionSchema, config),

  logout: (config?: AxiosRequestConfig) =>
    postRequest<ApiMutationResponse>(AUTH_ENDPOINTS.LOGOUT(), config),
};

// Helper function to build query parameters
export const buildQueryParams = (
  baseParams: Record<string, string | number | boolean | undefined> = {},
  filterParams: string[] = [],
): QueryParamsString => {
  const params = new URLSearchParams();

  Object.entries(baseParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  filterParams.forEach((param) => {
    if (!param) return;
    const eqIndex = param.indexOf('=');
    if (eqIndex === -1) return;
    const key = param.slice(0, eqIndex);
    const value = param.slice(eqIndex + 1);
    if (key && value) {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

// Rarity + pagination query string shared by the player-card endpoints
// (listed/auctions/rewards/instant). A falsy `limit` is omitted, matching the
// previous behavior where the API applies its own dynamic default.
const buildPlayerCardsParams = (rarity: string, limit: number): QueryParamsString =>
  buildQueryParams({
    [QUERY_PARAMS.RARITY]: rarity,
    [QUERY_PARAMS.OFFSET]: DEFAULT_VALUES.OFFSET,
    [QUERY_PARAMS.LIMIT]: limit || undefined,
  });
